import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { subDays, format } from 'date-fns';

// GraphQL query to get the count of unviewed vulnerability findings
// This counts by how cards are grouped in the UI rather than individual vulnerability entries
const GET_VULNERABILITY_FINDINGS_COUNT = gql`
  query GetVulnerabilityFindingsCount($startDate: timestamp!, $endDate: timestamp!) {
    job_runs(
      where: {
        is_vulnerable: {_eq: true},
        times: {_gte: $startDate, _lte: $endDate},
        viewed_at: {_is_null: true}
      },
      distinct_on: [target_id, job_name]
    ) {
      target_id
      job_name
    }
  }
`;

export const VulnerabilityFindingsCountContext = createContext(null);

export function VulnerabilityFindingsCountProvider({ children }) {
  const [count, setCount] = useState(0);
  
  // Memoize dates
  const startDate = useMemo(() => 
    format(subDays(new Date(), 30), 'yyyy-MM-dd'), 
  []);
  const endDate = useMemo(() => 
    format(new Date(), 'yyyy-MM-dd HH:mm:ss'), 
  []);

  // Updated query config
  const { data, loading, refetch } = useQuery(GET_VULNERABILITY_FINDINGS_COUNT, {
    variables: { startDate, endDate },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    pollInterval: 600000,
    notifyOnNetworkStatusChange: false,
    onError: (error) => console.error('Error fetching vulnerability findings count:', error)
  });

  // Remove manual polling logic
  useEffect(() => {
    const newCount = data?.job_runs?.length || 0;
    setCount(prev => prev !== newCount ? newCount : prev);
  }, [data]);

  // Use useMemo to memoize the context value
  const contextValue = useMemo(() => ({ 
    count, 
    setCount, 
    loading, 
    refetch 
  }), [count, loading, refetch]);

  return (
    <VulnerabilityFindingsCountContext.Provider value={contextValue}>
      {children}
    </VulnerabilityFindingsCountContext.Provider>
  );
}

VulnerabilityFindingsCountProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useVulnerabilityFindingsCount() {
  const context = useContext(VulnerabilityFindingsCountContext);
  if (!context) {
    throw new Error('useVulnerabilityFindingsCount must be used within a VulnerabilityFindingsCountProvider');
  }
  return context;
} 